/**
 * Supported printers. A model is listed only after it has been tested on real
 * hardware: label-printer firmware varies widely, and the wrong command can
 * jam a printer until it is power-cycled.
 */
import type { MonoBitmap } from "./bitmap.ts";
import { checkTspl, encodeTspl } from "./tspl.ts";

export interface Media {
  id: string;
  label: string;
  widthIn: number;
  heightIn: number;
  gapDots: number;
  /** Canvas width in dots: the printable width, padded as the driver pads rows. */
  canvasWidthDots: number;
  heightDots: number;
}

/** How a model talks over Bluetooth LE. */
export interface BleProfile {
  /** Advertised-name prefixes used to find the printer without a picker. */
  namePrefixes: readonly string[];
  /** Serial-over-BLE services to look for, in the order to try them. Lowercase 128-bit UUIDs. */
  services: readonly string[];
  /** Largest write proven on the hardware. Transports never exceed it. */
  maxChunkBytes: number;
  /** A pause of `pauseMs` after every `pauseEveryBytes`, so a small printer buffer keeps up. */
  pauseEveryBytes: number;
  pauseMs: number;
}

export interface PrinterModel {
  id: string;
  name: string;
  dpi: number;
  media: readonly Media[];
  encode(bitmap: MonoBitmap, media: Media, copies?: number): Promise<Uint8Array<ArrayBuffer>>;
  /** Returns a reason the job is unsafe for this model, or null. */
  check(job: Uint8Array): string | null;
  ble?: BleProfile;
  serial?: { baudRate: number };
}

const uuid16 = (hex: string) => `0000${hex}-0000-1000-8000-00805f9b34fb`;

export const JADENS_JD268BT: PrinterModel = {
  id: "jadens-jd268bt",
  name: "Jadens JD-268BT",
  dpi: 203,
  media: [
    // 2" x 1": 406 dots across, rows padded to 416 dots (52 bytes) as the Windows driver sends them.
    { id: "2x1", label: '2" × 1"', widthIn: 2, heightIn: 1, gapDots: 24, canvasWidthDots: 416, heightDots: 203 },
  ],
  encode: (bitmap, media, copies) => encodeTspl(bitmap, media, copies),
  check: checkTspl,
  ble: {
    namePrefixes: ["JD", "Jadens", "JADENS"],
    // The common printer services first, then the two the JD-268BT publishes in
    // its service records (FEE7 and the "WE CHAT" 128-bit one).
    services: [
      uuid16("18f0"),
      uuid16("ff00"),
      uuid16("ffe0"),
      uuid16("ae30"),
      uuid16("ffb0"),
      "49535343-fe7d-4ae5-8fa9-9fafd205e455",
      "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
      uuid16("fee7"),
      "e5b152ed-6b46-09e9-4678-665e9a972cbc",
    ],
    // 20 bytes is the smallest BLE payload and is what has been proven. Larger
    // writes over a negotiated MTU are measured in the phase 3 iPhone spike.
    maxChunkBytes: 20,
    pauseEveryBytes: 400,
    pauseMs: 15,
  },
  // Ignored by a Bluetooth serial link, but Web Serial requires a rate.
  serial: { baudRate: 9600 },
};

export const PRINTERS: readonly PrinterModel[] = [JADENS_JD268BT];

export function printerById(id: string): PrinterModel | undefined {
  return PRINTERS.find((p) => p.id === id);
}
